/**
 * ProductImageGallery (V3-S1) — the ONE shared tile row for
 * `ProductImageSlot[]` (max 5), mounted by BOTH the detail Images section and
 * the product-wizard step ③.
 *
 * Tile state machine (house preview rule — never a stuck spinner):
 * - loading → Spin over a `colorFillQuaternary` tile while the <img> loads;
 * - loaded  → the image (cover-fit, antd Image preview on click) + caption;
 * - error   → NEUTRAL retry tile (quaternary bg, tertiary icon — not
 *   red-alarm) with a quiet Retry that re-mounts the <img>.
 * URL rows render the address as a clickable link (target=_blank — her
 * 點網址可開超連結); data-URI rows show 上傳的檔案 instead (provenance is
 * DERIVED via `isDataUri`, never stored — V3-Δ0).
 *
 * `pending` tiles carry the wizard's in-flight file reads (reading / failed)
 * — the read promise always settles a tile to loaded-or-error (V3-S3).
 */

import { useState } from 'react';
import { Button, Dropdown, Image, Spin, Typography, theme } from 'antd';
import { FileImageOutlined, MoreOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { radiusTokens } from '@/styles/tokens';
import { isDataUri } from '@/utils/imageFileUtils';

const { Text } = Typography;

const TILE_SIZE = 96;
const COLUMN_WIDTH = 128;

/** Structural superset of ProductImageSlot / the wizard's ImageSlotRow. */
export interface ProductGalleryImage {
  slot: number;
  url: string;
  caption?: string;
}

/** Wizard file-read tile — settled by the read pipeline, never left reading. */
export interface PendingImageTile {
  id: string;
  fileName: string;
  status: 'reading' | 'failed';
  /** i18n leaf under products:images.* — set when status === 'failed'. */
  errorKey?: 'tooLarge' | 'notImage' | 'readFailed' | 'maxReached';
  onRetry?: () => void;
  onRemove?: () => void;
}

interface ProductImageGalleryProps {
  images: readonly ProductGalleryImage[];
  editable?: boolean;
  maxImages?: number;
  onAdd?: () => void;
  onEdit?: (image: ProductGalleryImage) => void;
  onRemove?: (slot: number) => void;
  pending?: readonly PendingImageTile[];
}

type TileLoadState = 'loading' | 'loaded' | 'error';

export const ProductImageGallery = ({
  images,
  editable = false,
  maxImages = 5,
  onAdd,
  onEdit,
  onRemove,
  pending = [],
}: ProductImageGalleryProps) => {
  const { t } = useTranslation(['products', 'common']);
  const { token } = theme.useToken();

  // Load state per tile, keyed by url so an edited row restarts at loading;
  // `attempts` remounts the <img> on Retry.
  const [loadStates, setLoadStates] = useState<Record<string, TileLoadState>>({});
  const [attempts, setAttempts] = useState<Record<string, number>>({});

  const tileBaseStyle: React.CSSProperties = {
    width: TILE_SIZE,
    height: TILE_SIZE,
    borderRadius: radiusTokens.card,
    border: `1px solid ${token.colorBorderSecondary}`,
    overflow: 'hidden',
    position: 'relative',
    backgroundColor: token.colorFillQuaternary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 'none',
  };

  const setLoadState = (key: string, state: TileLoadState) =>
    setLoadStates((current) => (current[key] === state ? current : { ...current, [key]: state }));

  const renderNeutralErrorTile = (
    reasonText: string,
    retryLabel: string | null,
    onRetry: (() => void) | undefined
  ) => (
    <>
      <div style={tileBaseStyle}>
        <FileImageOutlined style={{ fontSize: token.sizeMD, color: token.colorTextTertiary }} />
      </div>
      <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
        {reasonText}
      </Text>
      {retryLabel && onRetry && (
        <Button
          type="link"
          style={{ paddingLeft: 0, paddingRight: 0, minHeight: 32, alignSelf: 'flex-start' }}
          onClick={onRetry}
        >
          {retryLabel}
        </Button>
      )}
    </>
  );

  const renderImageTile = (image: ProductGalleryImage) => {
    const stateKey = `${image.slot}:${image.url}`;
    const state = loadStates[stateKey] ?? 'loading';
    const attempt = attempts[stateKey] ?? 0;
    const uploaded = isDataUri(image.url);
    // Accessible name: never the raw URL for uploaded rows — an uncaptioned
    // data URI would put kilobytes of base64 into alt / the button's name.
    const accessibleName = image.caption?.trim()
      ? image.caption
      : uploaded
        ? t('products:images.uploadedFile')
        : image.url;

    return (
      <div
        key={stateKey}
        style={{ width: COLUMN_WIDTH, display: 'flex', flexDirection: 'column', gap: 4 }}
      >
        {state === 'error' ? (
          renderNeutralErrorTile(t('products:images.loadFailed'), t('products:images.retry'), () => {
            setAttempts((current) => ({ ...current, [stateKey]: attempt + 1 }));
            setLoadState(stateKey, 'loading');
          })
        ) : (
          <div style={tileBaseStyle}>
            <Image
              key={`${stateKey}:${attempt}`}
              src={image.url}
              alt={accessibleName}
              width={TILE_SIZE}
              height={TILE_SIZE}
              style={{ objectFit: 'cover' }}
              preview={state === 'loaded'}
              onLoad={() => setLoadState(stateKey, 'loaded')}
              onError={() => setLoadState(stateKey, 'error')}
            />
            {state === 'loading' && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: token.colorFillQuaternary,
                }}
              >
                <Spin size="small" />
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Text ellipsis style={{ fontSize: token.fontSizeSM, flex: 1 }}>
            {image.caption?.trim() ? image.caption : '-'}
          </Text>
          {editable && (onEdit || onRemove) && (
            <Dropdown
              menu={{
                items: [
                  ...(onEdit
                    ? [
                        {
                          key: 'edit',
                          label: t('common:actions.edit'),
                          onClick: () => onEdit(image),
                        },
                      ]
                    : []),
                  ...(onRemove
                    ? [
                        {
                          key: 'delete',
                          label: t('common:actions.delete'),
                          danger: true,
                          onClick: () => onRemove(image.slot),
                        },
                      ]
                    : []),
                ],
              }}
              trigger={['click']}
            >
              <Button
                type="text"
                size="small"
                aria-label={t('products:actionsFor', { name: accessibleName })}
                icon={<MoreOutlined />}
              />
            </Dropdown>
          )}
        </div>

        {uploaded ? (
          <Text type="secondary" style={{ fontSize: token.fontSizeSM }} ellipsis>
            {t('products:images.uploadedFile')}
          </Text>
        ) : (
          <a
            href={image.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontSize: token.fontSizeSM,
              color: token.colorPrimary,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
            }}
            title={image.url}
          >
            {image.url}
          </a>
        )}
      </div>
    );
  };

  const renderPendingTile = (tile: PendingImageTile) => (
    <div
      key={tile.id}
      style={{ width: COLUMN_WIDTH, display: 'flex', flexDirection: 'column', gap: 4 }}
    >
      {tile.status === 'reading' ? (
        <>
          <div style={tileBaseStyle}>
            <Spin size="small" />
          </div>
          <Text type="secondary" style={{ fontSize: token.fontSizeSM }} ellipsis>
            {tile.fileName}
          </Text>
        </>
      ) : (
        <>
          {renderNeutralErrorTile(
            t(`products:images.${tile.errorKey ?? 'readFailed'}`),
            tile.onRetry ? t('products:images.retry') : null,
            tile.onRetry
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Text type="secondary" style={{ fontSize: token.fontSizeSM, flex: 1 }} ellipsis>
              {tile.fileName}
            </Text>
            {tile.onRemove && (
              <Button
                type="text"
                size="small"
                danger
                style={{ padding: 0, height: 'auto' }}
                onClick={tile.onRemove}
              >
                {t('common:actions.delete')}
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );

  const atCapacity = images.length + pending.length >= maxImages;

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: token.marginMD, alignItems: 'flex-start' }}>
      {images.map(renderImageTile)}
      {pending.map(renderPendingTile)}
      {editable && onAdd && !atCapacity && (
        <button
          type="button"
          aria-label={t('products:images.addByUrl')}
          onClick={onAdd}
          style={{
            ...tileBaseStyle,
            flexDirection: 'column',
            gap: 4,
            border: `1px dashed ${token.colorBorder}`,
            backgroundColor: 'transparent',
            cursor: 'pointer',
            color: token.colorTextSecondary,
            fontSize: token.fontSizeSM,
          }}
        >
          <PlusOutlined style={{ fontSize: token.sizeSM }} />
          {t('products:images.addByUrl')}
        </button>
      )}
    </div>
  );
};

export default ProductImageGallery;
